import * as React from 'react';
import { DataGrid, getGridStringOperators } from '@mui/x-data-grid';
import { makeStyles } from "@mui/styles";
import Box from '@mui/material/Box';
import { useList } from "hooks/useList";
import { getDomains, deleteDomainById } from "services/domains";
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import GridToolbar from 'components/GridToolbar';
import LinearProgress from '@mui/material/LinearProgress';
import ContentHeader from 'components/ContentHeader';
import CustomButton from 'components/CustomButton';
import ListPagination from 'components/ListPagination';
import ListQuickSearch from 'components/ListQuickSearch';
import { getListActions, checkCreatePermission } from "utils/listActions";

const useStyles = makeStyles((theme) => ({
    tableHeader: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
    }
}));

const BaseFilters = { includeTotal: true };

export default function Domains() {
    const classes = useStyles();
    const [activeItemId, setActiveItemId] = React.useState(null);

    const {
        loading,
        items,
        total,
        editItem,
        deleteItem,
        filters,
        setFilters,
        setSortModel,
        setPageNo,
        setPageSize,
    } = useList({
        getItems: getDomains,
        initialPageNo: 0,
        initialPageSize: 8,
        itemName: "interviewDomains",
        deleteItemById: deleteDomainById,
        initialFilters: BaseFilters,
    });

    const handleFilterChange = ({ items }) => {
        const { columnField, value } = items[0];
        if (value)
            setFilters({ [columnField]: value });
    };

    const handleSortModelChange = (newModel) => {
        if (newModel[0]) {
            const { field, sort } = newModel[0];
            setSortModel({ orderBy: field, order: sort });
        }
    };

    const handleConfirmationSuccess = () => {
        deleteItem(activeItemId);
        setActiveItemId(null);
    };

    const columns = React.useMemo(() => {
        return [
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                headerClassName: classes.tableHeader,
                filterOperators: getGridStringOperators().filter(
                    (operator) => operator.value === 'contains',
                ),
            },
            {
                field: 'code',
                headerName: 'Code',
                flex: 1,
                headerClassName: classes.tableHeader,
                filterOperators: getGridStringOperators().filter(
                    (operator) => operator.value === 'contains',
                ),
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: "actions",
                width: 80,
                headerClassName: classes.tableHeader,
                getActions: (params) =>
                    getListActions(params.id, editItem, setActiveItemId, "InterviewDomain")
            },
        ];
    }, []);

    return (
        <Box>
            <ContentHeader
                title={"Domains"}
                clearFilter
                BaseFilters={BaseFilters}
                filters={filters}
                setFilters={setFilters}
            >
                <ListQuickSearch onFilterChange={handleFilterChange} />
                &nbsp;
                {checkCreatePermission("InterviewDomain") && <CustomButton
                    variant="contained"
                    onClick={editItem}
                >
                    Create
                </CustomButton>}
            </ContentHeader>
            <div style={{ height: '80vh', width: '100%', padding: 16 }}>
                <DataGrid
                    pagination
                    autoPageSize
                    paginationMode='server'
                    filterMode='server'
                    density='compact'
                    loading={loading}
                    rowCount={total}
                    onPageChange={setPageNo}
                    onPageSizeChange={setPageSize}
                    rows={items}
                    getRowId={(row) => row._id}
                    columns={columns}
                    sortingMode="server"
                    onSortModelChange={handleSortModelChange}
                    onFilterModelChange={handleFilterChange}
                    components={{
                        Toolbar: GridToolbar,
                        LoadingOverlay: LinearProgress,
                        Pagination: ListPagination,
                    }}
                    componentsProps={{
                        pagination: {
                            rowCount: total
                        }
                    }}
                />
            </div>
            <ConfirmationDialog
                message={
                    `This domain will permanantly get deleted, Are you sure?`
                }
                open={!!activeItemId}
                title="Delete Interview Domain"
                primaryActionVariant="error"
                primaryAction="Yes"
                secondaryAction="Cancel"
                onClose={() => setActiveItemId(null)}
                onSuccess={handleConfirmationSuccess}
            />
        </Box>
    );
}